import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import type { homeMultimediaContentItemContent$key } from "~/queries/__generated__/homeMultimediaContentItemContent.graphql";

import { getLabel } from "./helpers";
import {
  Container,
  Content,
  CoverImage,
  GalleryContainer,
  GalleryTotal,
  Headline,
  Label,
  StyledEntityLink,
  StyledGalleryIcon,
} from "./styles";

export type Props = {
  reference: homeMultimediaContentItemContent$key;
} & ContentItemRenderVariantProps;

export const ContentItemHomeMultimedia = forwardRef<HTMLDivElement, Props>(
  ({ className, onClick, provider, reference: reference_ }, reference) => {
    const content = useFragment(
      graphql`
        fragment homeMultimediaContentItemContent on Content {
          entityId
          ... on Gallery {
            images {
              __typename
            }
          }
          ...entityLink
          ...helpersGetLabelContent
        }
      `,
      reference_,
    );

    const label = getLabel(content);

    const galleryTotalImages = content?.images?.length;

    const isVideo = label === "Video";

    return (
      <Container className={className} ref={reference}>
        <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
          <Content>
            <Label>{label}</Label>
            <Headline>
              {provider.headline({ preferSocialHeadline: true, useContentSponsorStyle: false })}
            </Headline>
          </Content>
          <CoverImage $isVideo={isVideo}>
            {provider.coverImage({
              responsiveVariants: {
                desktopUp: "size320x180",
                homeDesktopUp: "size320x180",
                mobileUp: "size320x180",
                tabletUp: "size320x180",
              },
              withCaption: true,
            })}
            {provider.video?.durationOverlay({
              variant: "homepage",
            })}
            {galleryTotalImages && (
              <GalleryContainer>
                <StyledGalleryIcon />
                <GalleryTotal>{galleryTotalImages}</GalleryTotal>
              </GalleryContainer>
            )}
          </CoverImage>
        </StyledEntityLink>
      </Container>
    );
  },
);

ContentItemHomeMultimedia.displayName = "ContentItemHomeMultimedia";
